import React from 'react';
import PropTypes from 'prop-types';

import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { toLower } from 'lodash';

import { post } from 'helpers/api';
import ManagedInput from 'components/form/managed_input';
import Button from 'shared/button';

export default class CreateModal extends React.Component {
  static propTypes = {
    listings: PropTypes.arrayOf(PropTypes.shape({
      address: PropTypes.string,
      createVoucherPath: PropTypes.string,
      id: PropTypes.number,
      number: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.string,
      typeLabel: PropTypes.string,
    })),
    show: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onNewVoucher: PropTypes.func.isRequired,
  }

  state = {}

  handleSubmit = () => {
    const { voucher } = this.state;

    if (voucher) {
      window.location = voucher.path;
    }
  }

  handleCreateVoucher = () => {
    const { onNewVoucher } = this.props;
    const listing = (this.props.listings.length > 1) ? this.state.listing : this.props.listings[0];

    this.setState({ pendingRequest: true });

    post(listing.createVoucherPath)
      .then(({ voucher }) => {
        onNewVoucher(voucher);
        this.setState({ pendingRequest: false, voucher });
      })
      .catch(() => {
        // Show erorr
      });
  }

  handleCancel = () => {
    this.setState({
      listing: null,
      pendingRequest: false,
      voucher: null,
    });

    this.props.onCancel();
  }

  handleValueChange = (listingId) => {
    const { listings } = this.props;
    const listing = listings.find(listing => listing.id == listingId);

    this.setState({ listing });
  }

  render() {
    const { show } = this.props;

    return (
      <Modal
        backdrop="static"
        className="voucher-creation-modal"
        name="create-modal"
        show={show}
        onHide={this.handleCancel}
      >
        <Modal.Header closeButton>
          <h3 className="mt-2 mb-0">New Voucher</h3>
        </Modal.Header>
        <React.Fragment>
          <Modal.Body>
            {this.renderBody()}
          </Modal.Body>
          <Modal.Footer>
            {this.state.voucher ? null : (
              <Button.Cancel name="cancel-button" onClick={this.handleCancel} />
            )}
            {this.renderSubmitButton()}
          </Modal.Footer>
        </React.Fragment>
      </Modal>
    );
  }

  renderBody() {
    const { listings } = this.props;
    const { listing, voucher } = this.state;

    if (listings == null) return;

    if (voucher) {
      return this.renderVoucherCreatedBody(voucher);
    } else if (listing && listing.voucher && listing.voucher.path) {
      return (
        <React.Fragment>
          {this.renderPickerBody(listings)}
          {this.renderVoucherExistWarning(listing.voucher)}
        </React.Fragment>
      );
    } else if (listings.length > 1) {
      return this.renderPickerBody(listings);
    } else if (listings[0].voucher && listings[0].voucher.path) {
      return this.renderVoucherExistWarning(listings[0].voucher);
    } else {
      return this.renderConfirmationBody(listings[0]);
    }
  }

  renderVoucherExistWarning({ oneLineLabel, path  }) {
    return (
      <div className="existing-voucher bg-warning" name="voucher-exist-warning">
        <FontAwesomeIcon className="mb-2 text-muted" icon={faExclamationTriangle} size="lg" />
        <div>A voucher for <strong>{oneLineLabel}</strong> has already been created</div>
        <a href={path}>View Voucher</a>
      </div>
    );
  }

  renderSubmitButton() {
    const { listing, pendingRequest } = this.state;
    const { listings } = this.props;
    let listingVoucher;

    if (listings == null) return;


    if (listings.length == 1 && listings[0].voucher && listings[0].voucher.path) {
      listingVoucher = listings[0].voucher;
    }

    if (listing && listing.voucher && listing.voucher.path) {
      listingVoucher = listing.voucher;
    }

    const voucher = this.state.voucher || listingVoucher;

    if (voucher) {
      return (
        <Button.Primary name="view-voucher-button" onClick={() => window.location = voucher.path}>
          View Voucher
        </Button.Primary>
      );
    } else {
      const disabled = pendingRequest || (!listing && listings.length > 1);

      return (
        <Button.Primary
          disabled={disabled}
          name="create-voucher-button"
          onClick={this.handleCreateVoucher}
        >
          Create Voucher
        </Button.Primary>
      );
    }
  }

  renderPickerBody(listings) {
    const { listing } = this.state;
    let label, getLabel;

    if (listings && listings[0].type == 'LotListing') {
      label = 'Lot';
      getLabel = listing => listing.number;
    } else if (listings && listings[0].type == 'CondoListing') {
      label = 'Condo';
      getLabel = listing => listing.address2 || listing.number;
    } else {
      label = 'Lease Space';
      getLabel = listing => listing.address2;
    }

    const options = listings.map(listing => (
      <option key={listing.id} value={listing.id}>
        {getLabel(listing)}
      </option>
    ));

    return (
      <div className="form form-horizontal" name="listing-picker">
        <div className="form-group select required">
          <label className="control-label" name="picker-label">{label}</label>
          <div className="form-input-container">
            <ManagedInput
              className="form-control select"
              includeBlank
              name="listing-select"
              type="select"
              value={listing && listing.id}
              onValueChange={this.handleValueChange}
            >
              {options}
            </ManagedInput>
          </div>
        </div>
      </div>
    );
  }

  renderVoucherCreatedBody(voucher) {
    const { oneLineLabel, statusPillClass, statusLabel } = voucher;

    return (
      <div name="creation-results">
        <p>A voucher for <strong>{oneLineLabel}</strong> was successfully created.</p>
        <p>
          Voucher status:
          <label className={classNames('label sm pill-label label-voucher ml-2', statusPillClass)}>
            {statusLabel}
          </label>
        </p>
      </div>
    );
  }

  renderConfirmationBody(listing) {
    const { voucher } = listing;
    const label = <strong>{voucher.oneLineLabel || ''}</strong>;

    return (
      <p name="confirmation">
        Are you sure you want to create a new {toLower(listing.typeLabel)} voucher for {label}?
      </p>
    );
  }
}
